/**
 * Admin → Users → "Tabs": choose which finance tabs a user can see.
 *
 * A checked box = the user CAN see that tab. Unchecking hides it — the pill
 * disappears and the route is blocked for that user (app-level, not Entra).
 * Everything is checked by default; this screen only ever restricts.
 */
import { useEffect, useState } from "react";
import { getUserTabs, saveUserTabs, type AppUser, type FinanceTab } from "../api";

export default function TabAccessModal({
  user, onClose, onSaved,
}: {
  user: AppUser;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [tabs, setTabs] = useState<FinanceTab[] | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let dead = false;
    void (async () => {
      try {
        const data = await getUserTabs(user.id);
        if (dead) return;
        setTabs(data.tabs);
        setHidden(new Set(data.hidden));
      } catch (e) {
        if (!dead) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { dead = true; };
  }, [user.id]);

  const toggle = (key: string, visible: boolean) =>
    setHidden((h) => {
      const next = new Set(h);
      if (visible) next.delete(key); else next.add(key);
      return next;
    });

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await saveUserTabs(user.id, [...hidden]);
      const shown = (tabs?.length ?? 0) - hidden.size;
      onSaved(`Tab access updated for ${user.display_name ?? user.upn} — ${shown} of ${tabs?.length ?? 0} tabs visible.`);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <header className="modal__head">
          <div>
            <h3>Tab access</h3>
            <p className="muted">{user.display_name ?? user.upn}</p>
          </div>
          <button className="modal__close" aria-label="Close" onClick={onClose}>×</button>
        </header>

        <div className="modal__body">
          <p className="muted">Ticked tabs are visible to this user. Unticked tabs are hidden and cannot be opened.</p>
          {error && <div className="alert alert--error">{error}</div>}
          {tabs === null ? (
            <p className="muted">Loading…</p>
          ) : (
            <ul className="tab-access-list">
              {tabs.map((t) => {
                const visible = !hidden.has(t.key);
                return (
                  <li key={t.key}>
                    <label>
                      <input type="checkbox" checked={visible} onChange={(e) => toggle(t.key, e.target.checked)} />
                      <span>{t.label}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="modal__foot">
          <button className="btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" disabled={busy || tabs === null} onClick={() => void save()}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
